import { useState, useEffect } from 'react';
import { type BootstrapEnv, type StackContext, isObjectStackContext } from './types';

/**
 * dev and test don't have the best solutions.
 * test - on the first pass, it actually gets prod since the persistedStackId will be undefined.
 *        it then reaches the prod bootstrap logic which sets the testStackId and reloads
 * dev - it shouldn't come here, it will go to localhost first. this means the fallback is not yet supported.
 * @param bootstrapEnv
 * @returns
 */
const getApiPath = (bootstrapEnv: BootstrapEnv): string => {
	if (bootstrapEnv === 'staging') {
		return 'staging';
	}
	const persistedTestStackId = window.sessionStorage.getItem('AUTOMATION_TEST_STACK_ID');
	if (bootstrapEnv === 'test' && typeof persistedTestStackId === 'string') {
		return persistedTestStackId;
	}

	return 'pro';
};

const getStackContextUrl = (cloudId: string, bootstrapEnv: BootstrapEnv) =>
	`/gateway/api/automation/internal-api/jira/${cloudId}/${getApiPath(bootstrapEnv)}/rest/stackcontext`;

type Loading = {
	status: 'loading';
	value: null;
	error: null;
};

type Success = {
	status: 'success';
	value: StackContext;
	error: null;
};

type Error = {
	status: 'error';
	value: null;
	error: string;
};

type StackContextResult = Loading | Success | Error;

const DEV_STACK_CONTEXT: StackContext = {
	// buildNumber is not actually used in dev since we don't redirect to bifrost, we serve from localhost
	buildNumber: 'automation-dev',
	staticPrefix: 'https://localhost:8088',
	perimeter: 'COMMERCIAL',
};

const useStackContext = (cloudId: string, bootstrapEnv: BootstrapEnv): StackContextResult => {
	const [stackContextResult, setStackContextResult] = useState<StackContextResult>({
		status: 'loading',
		value: null,
		error: null,
	});

	useEffect(() => {
		const abortController = new AbortController();

		const fetchStackContext = async () => {
			try {
				const response = await fetch(getStackContextUrl(cloudId, bootstrapEnv), {
					signal: abortController.signal,
				});
				const data: unknown = await response.json();
				if (!isObjectStackContext(data)) {
					throw new Error('Something went wrong loading Automation');
				}
				setStackContextResult({
					status: 'success',
					value: data,
					error: null,
				});
			} catch (e: unknown) {
				if (e instanceof Error) {
					// no need to set error if it was aborted due to clean up
					if (e.name === 'AbortError') {
						return;
					}
					setStackContextResult({
						status: 'error',
						value: null,
						error: e.message,
					});
				} else {
					setStackContextResult({
						status: 'error',
						value: null,
						error: String(e),
					});
				}
			}
		};
		if (bootstrapEnv !== 'dev') {
			fetchStackContext();
		}
		return () => {
			abortController.abort();
		};
	}, [cloudId, bootstrapEnv]);

	if (bootstrapEnv === 'dev') {
		return { status: 'success', error: null, value: DEV_STACK_CONTEXT };
	}

	return stackContextResult;
};

export default useStackContext;
